import Image from 'next/image'

const contentPadding = 'px-6 md:px-12'

function getPhotoUrl(photo: unknown): string | null {
  return photo && typeof photo === 'object' && 'url' in photo
    ? (photo as { url: string }).url
    : null
}

export interface TeamMember {
  name?: string | null
  designation?: string | null
  photo?: unknown
}

export interface TrustExpertiseSectionProps {
  locale: AppLocale
  memberCount?: string | null
  memberCountLabel?: string | null
  members?: TeamMember[] | null
}

const TRUST_TEXT: Record<
  AppLocale,
  { titleLine1: string; titleLine2: string; memberCountLabel: string; eyebrow: string }
> = {
  en: {
    eyebrow: 'TRUST & EXPERTISE',
    titleLine1: 'ADVANCED ENGINEERING BACKED BY',
    titleLine2: 'DECADES OF EXPERIENCE',
    memberCountLabel: 'AWESOME TEAM MEMBER IN GALLIUM FILTERS',
  },
  fr: {
    eyebrow: 'CONFIANCE ET EXPERTISE',
    titleLine1: 'INGÉNIERIE AVANCÉE SOUTENUE PAR',
    titleLine2: "DES DÉCENNIES D'EXPÉRIENCE",
    memberCountLabel: 'ÉQUIPE FORMIDABLE CHEZ GALLIUM FILTERS',
  },
  ar: {
    eyebrow: 'الثقة والخبرة',
    titleLine1: 'هندسة متقدمة مدعومة بـ',
    titleLine2: 'عقود من الخبرة',
    memberCountLabel: 'عضو فريق رائع في GALLIUM FILTERS',
  },
}

const BRAND_TOKEN_BY_LOCALE: Record<AppLocale, string> = {
  en: 'GALLIUM FILTERS',
  fr: 'FILTRES GALLIUM',
  ar: 'جاليوم فلاترز',
}

function localizeBrandToken(text: string, locale: AppLocale): string {
  return text.replace(/GALLIUM FILTERS/gi, BRAND_TOKEN_BY_LOCALE[locale])
}

function TeamCard({ member, placementClass }: { member: TeamMember; placementClass: string }) {
  const photo = getPhotoUrl(member.photo)
  return (
    <div
      className={`flex w-full max-w-[320px] flex-col items-start justify-self-center text-left md:max-w-[320px] ${placementClass}`}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-sm bg-neutral-200">
        {photo ? (
          <Image
            src={photo}
            alt={member.name ?? ''}
            fill
            className="object-cover object-top"
            sizes="(max-width: 768px) 50vw, 320px"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-neutral-500">
            Photo
          </div>
        )}
        <div className="absolute bottom-0 right-0 hidden h-10 w-10 shrink-0 items-center justify-center bg-[#FF0000] md:flex">
          <Image
            src="/icon/Symbol.svg"
            alt=""
            width={18}
            height={18}
            className="h-[18px] w-[18px]"
            aria-hidden
          />
        </div>
      </div>
      <div className="mt-0 flex w-full justify-end">
        <div className="h-2 w-10 shrink-0 bg-[#17262F]" aria-hidden />
      </div>
      <p className="font-poppins mt-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#FF0000]">
        <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-sm bg-[#FF0000]" aria-hidden />
        {member.designation ?? 'CEO GALLIUM'}
      </p>
      <p className="font-bebas-neue mt-0 text-base font-bold uppercase leading-tight text-[#111] md:text-lg">
        {member.name ?? 'JOHN MAXWELL'}
      </p>
    </div>
  )
}

export function TrustExpertiseSection({
  locale,
  memberCount = '5000+',
  memberCountLabel,
  members = [],
}: TrustExpertiseSectionProps) {
  const t = TRUST_TEXT[locale]
  const label = localizeBrandToken(memberCountLabel ?? t.memberCountLabel, locale)
  const words = label.replace(/\s+/g, ' ').split(' ')
  const line1 = words.slice(0, 3).join(' ')
  const line2 = words.slice(3).join(' ')
  const list = members ?? []
  const row1 = list.slice(0, 2)
  const row2 = list.slice(2, 5)

  return (
    <section className={`bg-white py-12 md:py-20 ${contentPadding}`}>
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col items-center text-center">
          <p className="font-poppins mb-2 text-xs font-semibold uppercase tracking-wider text-[#FF0000]">
            {t.eyebrow}
          </p>
          <h2 className="font-bebas-neue mb-2 text-2xl font-normal uppercase leading-tight text-[#111] sm:text-3xl lg:text-4xl">
            {t.titleLine1}
          </h2>
          <h2 className="font-bebas-neue mb-12 text-2xl font-normal uppercase leading-tight text-[#111] sm:text-3xl lg:text-4xl">
            {t.titleLine2}
          </h2>
        </div>
        <div className="mx-auto grid w-full max-w-5xl grid-cols-2 gap-4 px-2 sm:gap-6 md:grid-cols-4 md:grid-rows-[auto_auto] md:justify-items-center md:gap-6">
          {row1.map((member, i) => (
            <TeamCard
              key={i}
              member={member}
              placementClass={
                i === 0
                  ? 'col-start-1 row-start-1 md:col-start-1 md:row-start-1'
                  : 'col-start-2 row-start-1 md:col-start-2 md:row-start-1'
              }
            />
          ))}
          <div className="col-span-2 row-start-2 flex w-full flex-col items-center justify-center px-4 py-8 text-center sm:max-w-md sm:py-6 md:col-span-2 md:col-start-3 md:row-start-1 md:max-w-none md:px-0 md:py-0">
            <p
              className="font-bebas-neue text-7xl font-normal uppercase leading-none text-[#FF0000] sm:text-8xl lg:text-9xl xl:text-[10rem] 2xl:text-[12rem]"
              style={{ WebkitTextStroke: '2px #FF0000', color: 'transparent' }}
            >
              {memberCount}
            </p>
            <p className="font-bebas-neue mt-3 text-center text-xl font-normal uppercase leading-snug text-[#111] sm:mt-2 sm:text-2xl lg:mt-1 lg:text-3xl">
              {line1}
              <br />
              {line2}
            </p>
          </div>
          {row2.map((member, i) => (
            <TeamCard
              key={i}
              member={member}
              placementClass={
                i === 0
                  ? 'col-start-1 row-start-3 md:col-start-2 md:row-start-2'
                  : i === 1
                    ? 'col-start-2 row-start-3 md:col-start-3 md:row-start-2'
                    : 'col-start-1 row-start-4 md:col-start-4 md:row-start-2'
              }
            />
          ))}
        </div>
      </div>
    </section>
  )
}
